import React, { Fragment, useEffect, useRef, useState } from "react";
import useApi from "../../CustomHooks/useApi";
import { bankTransfersAPI } from "../../../api/apiClient";
import Loading from "../../../Loading";
import "../../../css/Views/BankFlowsView.css";

const monthNames = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
];

const FLOW_SECTION = {
    INCOME: 1,
    EXPENSE: 2,
    INTERNAL_TRANSFER: 3,
    UNIDENTIFIED: 4
};

const BankFlowsView = () => {
    const { callApi, isLoading } = useApi();

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const availableYears = Array.from(
        { length: currentYear - 2025 + 1 },
        (_, index) => currentYear - index
    );

    const [year, setYear] = useState(currentYear);
    const [flows, setFlows] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [viewMode, setViewMode] = useState("year");
    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [highlightedMonth, setHighlightedMonth] = useState(currentMonth);
    const [showTotal, setShowTotal] = useState(true);

    const [editingManualCell, setEditingManualCell] = useState(null);
    const [manualAmount, setManualAmount] = useState("");
    const [savingManualCell, setSavingManualCell] = useState(null);
    const manualInputRef = useRef(null);

    const fetchFlows = (clear = true) => {
        if (clear)
            setFlows(null);

        callApi(() => bankTransfersAPI.getFlows(year), (data) => {
            setFlows(data);
        });
    };

    useEffect(() => {
        setExpandedGroups({});
        setEditingManualCell(null);
        fetchFlows();
    }, [year]);

    useEffect(() => {
        if (editingManualCell && manualInputRef.current) {
            manualInputRef.current.focus();
            manualInputRef.current.select();
        }
    }, [editingManualCell]);

    const formatCurrency = (value) =>
        new Intl.NumberFormat("pl-PL", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value ?? 0);

    const formatTableValue = (value) => {
        if (value == null || Math.abs(value) < 0.005)
            return "—";

        return formatCurrency(value);
    };

    const handleYearChange = (event) => {
        setYear(Number(event.target.value));
    };

    const renderYearHeader = () => (
        <div className="bank-flows-year-header">
            <span>Rok</span>

            <select value={year} onChange={handleYearChange}>
                {availableYears.map((availableYear) => (
                    <option key={availableYear} value={availableYear}>
                        {availableYear}
                    </option>
                ))}
            </select>
        </div>
    );

    const getSolidarityFundRow = (name) =>
        flows?.solidarityFundRows?.find((row) => row.name === name);

    const getRowTotal = (row) =>
        row?.months?.reduce((sum, value) => sum + value, 0) ?? 0;

    const solidarityOpeningBalance = flows?.solidarityFundOpeningBalance ?? 0;

    const getManualCellKey = (row, monthIndex) =>
        `${row.manualValueType}-${monthIndex}`;

    const startManualEdit = (row, monthIndex) => {
        if (row.manualValueType == null)
            return;

        if (savingManualCell === getManualCellKey(row, monthIndex))
            return;

        setEditingManualCell({
            type: row.manualValueType,
            monthIndex,
            originalValue: row.months?.[monthIndex] ?? 0
        });

        setManualAmount(
            String(row.months?.[monthIndex] ?? 0).replace(".", ",")
        );
    };

    const cancelManualEdit = () => {
        setEditingManualCell(null);
        setManualAmount("");
    };

    const saveManualValue = async () => {
        if (!editingManualCell)
            return;

        const amount = Number(
            manualAmount
                .replace(/\s/g, "")
                .replace(",", ".")
        );

        if (Number.isNaN(amount)) {
            manualInputRef.current?.focus();
            return;
        }

        const {
            type,
            monthIndex,
            originalValue
        } = editingManualCell;

        const cellKey = `${type}-${monthIndex}`;

        if (amount === originalValue) {
            cancelManualEdit();
            return;
        }

        setEditingManualCell(null);
        setManualAmount("");
        setSavingManualCell(cellKey);

        try {
            await bankTransfersAPI.saveManualFlowValue({
                year,
                month: monthIndex + 1,
                type,
                amount
            });

            fetchFlows(false);
        } catch (saveError) {
            console.error(
                "Błąd podczas zapisywania wartości przepływu:",
                saveError
            );
        } finally {
            setSavingManualCell(null);
        }
    };

    const getGroupKey = (row) =>
        `${row.sectionId}-${row.groupId}`;

    const toggleGroup = (row) => {
        if (
            row.groupId == null ||
            !row.categories?.length
        )
            return;

        const key = getGroupKey(row);

        setExpandedGroups((current) => ({
            ...current,
            [key]: !current[key]
        }));
    };

    const getSectionClass = (row) => {
        if (row.sectionId === FLOW_SECTION.INCOME)
            return "bank-flows-income-row";

        if (row.sectionId === FLOW_SECTION.EXPENSE)
            return "bank-flows-expense-row";

        if (row.sectionId === FLOW_SECTION.INTERNAL_TRANSFER)
            return "bank-flows-internal-transfer-row";

        if (row.sectionId === FLOW_SECTION.UNIDENTIFIED)
            return "bank-flows-unidentified-row";

        return "";
    };

    const isCurrentMonthColumn = (monthIndex) =>
        year === currentYear &&
        monthIndex === currentMonth;

    const isHighlightedMonthColumn = (monthIndex) =>
        monthIndex === highlightedMonth;

    const visibleMonthIndexes =
        viewMode === "month"
            ? [selectedMonth]
            : monthNames.map((_, index) => index);

    const shouldShowTotal =
        viewMode === "year" &&
        showTotal;

    const renderManualCellContent = (
        row,
        monthIndex
    ) => {
        const cellKey =
            getManualCellKey(row, monthIndex);

        const isEditing =
            editingManualCell?.type === row.manualValueType &&
            editingManualCell?.monthIndex === monthIndex;

        const isSaving =
            savingManualCell === cellKey;

        if (isSaving) {
            return (
                <span
                    className="bank-flows-cell-spinner"
                    title="Zapisywanie..."
                />
            );
        }

        if (isEditing) {
            return (
                <div
                    className="bank-flows-inline-edit"
                    onClick={(event) =>
                        event.stopPropagation()
                    }
                >
                    <input
                        ref={manualInputRef}
                        type="text"
                        inputMode="decimal"
                        value={manualAmount}
                        onChange={(event) =>
                            setManualAmount(
                                event.target.value
                            )
                        }
                        onBlur={saveManualValue}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                event.preventDefault();
                                event.currentTarget.blur();
                            }

                            if (event.key === "Escape") {
                                event.preventDefault();
                                cancelManualEdit();
                            }
                        }}
                    />

                    <span>zł</span>
                </div>
            );
        }

        return formatTableValue(
            row.months?.[monthIndex] ?? 0
        );
    };

    const renderValueCell = (
        row,
        monthIndex,
        isManual
    ) => {
        const value =
            row.months?.[monthIndex] ?? 0;

        const isZero =
            Math.abs(value) < 0.005;

        const isHighlighted =
            isHighlightedMonthColumn(monthIndex);

        const cellKey =
            isManual
                ? getManualCellKey(
                    row,
                    monthIndex
                )
                : null;

        const isEditing =
            isManual &&
            editingManualCell?.type ===
                row.manualValueType &&
            editingManualCell?.monthIndex ===
                monthIndex;

        const isSaving =
            isManual &&
            savingManualCell === cellKey;

        return (
            <td
                key={monthIndex}
                className={[
                    isManual
                        ? "bank-flows-manual-cell"
                        : "",
                    isEditing
                        ? "bank-flows-manual-cell-editing"
                        : "",
                    isSaving
                        ? "bank-flows-manual-cell-saving"
                        : "",
                    isZero &&
                    !isEditing &&
                    !isSaving
                        ? "bank-flows-zero-cell"
                        : "",
                    isHighlighted
                        ? "bank-flows-highlighted-month-cell"
                        : ""
                ]
                    .filter(Boolean)
                    .join(" ")}
                title={
                    isManual &&
                    !isEditing &&
                    !isSaving
                        ? "Kliknij, aby zmienić wartość"
                        : undefined
                }
                onClick={(event) => {
                    if (
                        !isManual ||
                        isEditing ||
                        isSaving
                    )
                        return;

                    event.stopPropagation();

                    startManualEdit(
                        row,
                        monthIndex
                    );
                }}
            >
                {isManual
                    ? renderManualCellContent(
                        row,
                        monthIndex
                    )
                    : formatTableValue(value)}
            </td>
        );
    };

const renderTotalCell = (
    row,
    showIncrease = false
) => {
    const value = showIncrease
        ? row.increase
        : getRowTotal(row);

    const isZero =
        value == null ||
        Math.abs(value) < 0.005;

    return (
        <td
            className={[
                "bank-flows-total-column-cell",
                showIncrease && value > 0
                    ? "bank-flows-increase-positive"
                    : "",
                showIncrease && value < 0
                    ? "bank-flows-increase-negative"
                    : "",
                isZero
                    ? "bank-flows-zero-cell"
                    : ""
            ]
                .filter(Boolean)
                .join(" ")}
        >
            {value == null || isZero
                ? "—"
                : (
                    <>
                        {showIncrease && value > 0 ? "+" : ""}
                        {formatCurrency(value)}
                    </>
                )}
        </td>
    );
};

    const renderSingleRow = (
        row,
        className = "",
        isCategory = false,
        showIncrease = false
    ) => {
        const isManual =
            row.manualValueType != null;

        const isGroup =
            row.groupId != null &&
            row.categoryId == null &&
            row.categories?.length > 0;

        const isExpanded =
            isGroup &&
            expandedGroups[getGroupKey(row)];

        const sectionClass =
            getSectionClass(row);

        return (
            <tr
                key={`${className}-${row.sortOrder}-${row.groupId ?? "none"}-${row.categoryId ?? "none"}`}
                className={[
                    className,
                    sectionClass,
                    row.isTotal
                        ? "bank-flows-total-row"
                        : "",
                    isGroup
                        ? "bank-flows-group-row"
                        : "",
                    isExpanded
                        ? "bank-flows-group-expanded-row"
                        : "",
                    isCategory
                        ? "bank-flows-category-row"
                        : ""
                ]
                    .filter(Boolean)
                    .join(" ")}
                onClick={() =>
                    isGroup &&
                    toggleGroup(row)
                }
            >
                <td className="bank-flows-name-cell">
                    <div className="bank-flows-row-name">
                        {isCategory && (
                            <span className="bank-flows-category-indent" />
                        )}

                        <span className="bank-flows-row-name-text">
                            {row.name}
                        </span>

                        {isGroup && (
                            <span
                                className={`bank-flows-group-arrow ${
                                    isExpanded
                                        ? "is-expanded"
                                        : ""
                                }`}
                            >
                                ›
                            </span>
                        )}
                    </div>
                </td>

                {visibleMonthIndexes.map(
                    (monthIndex) =>
                        renderValueCell(
                            row,
                            monthIndex,
                            isManual
                        )
                )}

                {shouldShowTotal &&
                    renderTotalCell(
                        row,
                        showIncrease
                    )}
            </tr>
        );
    };

    const renderRow = (
        row,
        className = "",
        showIncrease = false
    ) => {
        const isExpanded =
            row.groupId != null &&
            expandedGroups[
                getGroupKey(row)
            ];

        return (
            <Fragment
                key={`${className}-${row.sortOrder}-${row.groupId ?? row.name}`}
            >
                {renderSingleRow(
                    row,
                    className,
                    false,
                    showIncrease
                )}

                {isExpanded &&
                    row.categories?.map(
                        (category) =>
                            renderSingleRow(
                                category,
                                className,
                                true,
                                showIncrease
                            )
                    )}
            </Fragment>
        );
    };

    const renderSectionHeader = (
        name,
        className
    ) => (
        <tr
            className={`bank-flows-section-row ${className}`}
        >
            <td
                colSpan={
                    visibleMonthIndexes.length +
                    1 +
                    (shouldShowTotal ? 1 : 0)
                }
            >
                {name}
            </td>
        </tr>
    );

    const handleMonthHeaderClick = (
        monthIndex
    ) => {
        setHighlightedMonth(monthIndex);

        if (viewMode === "month")
            setSelectedMonth(monthIndex);
    };

    const renderMonthHeaders = (
        options = {}
    ) => {
        const {
            showTotalColumn = true,
            allowHighlight = true
        } = options;

        return (
            <>
                {visibleMonthIndexes.map(
                    (monthIndex) => (
                        <th
                            key={monthIndex}
                            className={[
                                "bank-flows-month-header",
                                !allowHighlight
                                    ? "bank-flows-month-header-static"
                                    : "",
                                allowHighlight &&
                                isHighlightedMonthColumn(
                                    monthIndex
                                )
                                    ? "bank-flows-highlighted-month-header"
                                    : "",
                                isCurrentMonthColumn(
                                    monthIndex
                                )
                                    ? "bank-flows-current-month-header"
                                    : ""
                            ]
                                .filter(Boolean)
                                .join(" ")}
                            onClick={
                                allowHighlight
                                    ? () =>
                                        handleMonthHeaderClick(
                                            monthIndex
                                        )
                                    : undefined
                            }
                        >
                            {isCurrentMonthColumn(
                                monthIndex
                            ) && (
                                <span className="bank-flows-current-month-label">
                                    TERAZ
                                </span>
                            )}

                            <span className="bank-flows-month-header-name">
                                {
                                    monthNames[
                                        monthIndex
                                    ]
                                }
                            </span>
                        </th>
                    )
                )}

                {shouldShowTotal &&
                    showTotalColumn && (
                        <th className="bank-flows-total-column-header">
                            Razem
                        </th>
                    )}
            </>
        );
    };

    const renderViewControls = () => (
        <div className="bank-flows-toolbar">
            <div className="bank-flows-view-switch">
                <div
                    className={`bank-flows-view-switch-option ${
                        viewMode === "year"
                            ? "is-active"
                            : ""
                    }`}
                    onClick={() =>
                        setViewMode("year")
                    }
                >
                    Cały rok
                </div>

                <div
                    className={`bank-flows-view-switch-option ${
                        viewMode === "month"
                            ? "is-active"
                            : ""
                    }`}
                    onClick={() => {
                        setViewMode("month");
                        setSelectedMonth(
                            highlightedMonth
                        );
                    }}
                >
                    Miesiąc
                </div>
            </div>

            {viewMode === "month" && (
                <select
                    className="bank-flows-month-select"
                    value={selectedMonth}
                    onChange={(event) => {
                        const monthIndex =
                            Number(
                                event.target.value
                            );

                        setSelectedMonth(
                            monthIndex
                        );

                        setHighlightedMonth(
                            monthIndex
                        );
                    }}
                >
                    {monthNames.map(
                        (month, index) => (
                            <option
                                key={month}
                                value={index}
                            >
                                {month}
                            </option>
                        )
                    )}
                </select>
            )}

            {viewMode === "year" && (
                <div
                    className={`bank-flows-total-toggle ${
                        showTotal
                            ? "is-active"
                            : ""
                    }`}
                    onClick={() =>
                        setShowTotal(
                            (current) =>
                                !current
                        )
                    }
                >
                    <span className="bank-flows-total-toggle-box">
                        {showTotal && "✓"}
                    </span>

                    <span>Podsumowanie roczne</span>
                </div>
            )}
        </div>
    );

    const getSolidarityValueForView = (
        row
    ) => {
        if (!row)
            return 0;

        if (viewMode === "month")
            return (
                row.months?.[
                    selectedMonth
                ] ?? 0
            );

        return getRowTotal(row);
    };

    const solidarityBalanceRow =
        getSolidarityFundRow("Stan");

    const solidarityDisplayedIncome =
        getSolidarityValueForView(
            getSolidarityFundRow(
                "Wpływy na fundusz"
            )
        );

    const solidarityDisplayedSubsidies =
        getSolidarityValueForView(
            getSolidarityFundRow(
                "Przyznane dofinansowania"
            )
        );

    const solidarityDisplayedReturns =
        getSolidarityValueForView(
            getSolidarityFundRow(
                "Zwroty dofinansowań"
            )
        );

    const solidarityDisplayedBalance =
        viewMode === "month"
            ? solidarityBalanceRow
                  ?.months?.[
                      selectedMonth
                  ] ?? 0
            : solidarityBalanceRow
                  ?.months?.at(-1) ?? 0;

    return (
        <div className="bank-flows-view">
            {isLoading &&
                !flows && (
                    <Loading text="Ładowanie przepływów..." />
                )}

            

            {flows && (
                    <>
                        {renderViewControls()}

                        <div
                            className={`bank-flows-table-wrapper ${
                                viewMode ===
                                "month"
                                    ? "bank-flows-month-mode"
                                    : ""
                            }`}
                        >
                            <table className="bank-flows-table">
                                <thead>
                                    <tr>
                                        <th className="bank-flows-name-column">
                                            {renderYearHeader()}
                                        </th>

                                        {renderMonthHeaders()}
                                    </tr>
                                </thead>

                                <tbody>
                                    {flows.rows?.map(
                                        (row) =>
                                            renderRow(
                                                row,
                                                "bank-flows-main-row"
                                            )
                                    )}

                                    {flows
                                        .internalTransferRows
                                        ?.length >
                                        0 && (
                                        <>
                                            {renderSectionHeader(
                                                "PRZESUNIĘCIA ŚRODKÓW",
                                                "bank-flows-internal-transfer-section-row"
                                            )}

                                            {flows.internalTransferRows.map(
                                                (
                                                    row
                                                ) =>
                                                    renderRow(
                                                        row
                                                    )
                                            )}
                                        </>
                                    )}

                                    {flows
                                        .unidentifiedRows
                                        ?.length >
                                        0 && (
                                        <>
                                            {renderSectionHeader(
                                                "NIEZIDENTYFIKOWANE",
                                                "bank-flows-unidentified-section-row"
                                            )}

                                            {flows.unidentifiedRows.map(
                                                (
                                                    row
                                                ) =>
                                                    renderRow(
                                                        row
                                                    )
                                            )}
                                        </>
                                    )}

                                    {flows.otherRows
                                        ?.length >
                                        0 && (
                                        <>
                                            {renderSectionHeader(
                                                "INNE",
                                                "bank-flows-other-section-row"
                                            )}

                                            {flows.otherRows.map(
                                                (
                                                    row
                                                ) =>
                                                    renderRow(
                                                        row
                                                    )
                                            )}
                                        </>
                                    )}

                                    {flows
                                        .balanceRows
                                        ?.length >
                                        0 && (
                                        <>
                                            {renderSectionHeader(
                                                "BILANS",
                                                "bank-flows-balance-section-row"
                                            )}

                                            {flows.balanceRows.map(
                                                (
                                                    row
                                                ) =>
                                                    renderRow(
                                                        row,
                                                        "bank-flows-balance-row",
                                                        true
                                                    )
                                            )}
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {flows.solidarityFundRows
                            ?.length >
                            0 && (
                            <div className="bank-flows-solidarity-section">
                                <div className="bank-flows-solidarity-header-row">
                                    <div className="bank-flows-solidarity-heading">
                                        <div className="bank-flows-solidarity-header">
                                            FUNDUSZ SOLIDARNOŚCIOWY
                                        </div>

                                        <div className="bank-flows-solidarity-period">
                                            {viewMode ===
                                            "month"
                                                ? `${monthNames[selectedMonth]} ${year}`
                                                : `Podsumowanie ${year}`}
                                        </div>
                                    </div>

                                    <div className="bank-flows-solidarity-stats">
                                        <div className="bank-flows-solidarity-stat">
                                            <span>
                                                Stan początkowy
                                            </span>

                                            <strong>
                                                {formatCurrency(
                                                    solidarityOpeningBalance
                                                )}{" "}
                                                zł
                                            </strong>
                                        </div>

                                        <div className="bank-flows-solidarity-stat">
                                            <span>
                                                Wpływy
                                            </span>

                                            <strong className="bank-flows-solidarity-positive">
                                                {solidarityDisplayedIncome >
                                                0
                                                    ? "+"
                                                    : ""}
                                                {formatCurrency(
                                                    solidarityDisplayedIncome
                                                )}{" "}
                                                zł
                                            </strong>
                                        </div>

                                        <div className="bank-flows-solidarity-stat">
                                            <span>
                                                Przyznane dofinansowania
                                            </span>

                                            <strong>
                                                {formatCurrency(
                                                    solidarityDisplayedSubsidies
                                                )}{" "}
                                                zł
                                            </strong>
                                        </div>

                                        <div className="bank-flows-solidarity-stat">
                                            <span>
                                                Zwroty
                                            </span>

                                            <strong className="bank-flows-solidarity-positive">
                                                {solidarityDisplayedReturns >
                                                0
                                                    ? "+"
                                                    : ""}
                                                {formatCurrency(
                                                    solidarityDisplayedReturns
                                                )}{" "}
                                                zł
                                            </strong>
                                        </div>

                                        <div className="bank-flows-solidarity-stat bank-flows-solidarity-balance">
                                            <span>
                                                Stan funduszu
                                            </span>

                                            <strong>
                                                {formatCurrency(
                                                    solidarityDisplayedBalance
                                                )}{" "}
                                                zł
                                            </strong>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className={`bank-flows-table-wrapper ${
                                        viewMode ===
                                        "month"
                                            ? "bank-flows-month-mode"
                                            : ""
                                    }`}
                                >
                                    <table className="bank-flows-table bank-flows-solidarity-table">
                                        <thead>
                                            <tr>
                                                <th className="bank-flows-name-column">
                                                    {renderYearHeader()}
                                                </th>

                                                {renderMonthHeaders(
                                                    {
                                                        showTotalColumn:
                                                            false,
                                                        allowHighlight:
                                                            false
                                                    }
                                                )}
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {flows.solidarityFundRows.map(
                                                (
                                                    row
                                                ) => {
                                                    const isBalanceRow =
                                                        row.name ===
                                                        "Stan";

                                                    return (
                                                        <tr
                                                            key={
                                                                row.sortOrder
                                                            }
                                                            className={
                                                                isBalanceRow
                                                                    ? "bank-flows-solidarity-balance-row"
                                                                    : ""
                                                            }
                                                        >
                                                            <td className="bank-flows-name-cell">
                                                                {
                                                                    row.name
                                                                }
                                                            </td>

                                                            {visibleMonthIndexes.map(
                                                                (
                                                                    monthIndex
                                                                ) => {
                                                                    const value =
                                                                        row
                                                                            .months?.[
                                                                            monthIndex
                                                                        ] ??
                                                                        0;

                                                                    const isZero =
                                                                        Math.abs(
                                                                            value
                                                                        ) <
                                                                        0.005;

                                                                    return (
                                                                        <td
                                                                            key={
                                                                                monthIndex
                                                                            }
                                                                            className={
                                                                                isZero
                                                                                    ? "bank-flows-zero-cell"
                                                                                    : ""
                                                                            }
                                                                        >
                                                                            {formatTableValue(
                                                                                value
                                                                            )}
                                                                        </td>
                                                                    );
                                                                }
                                                            )}
                                                        </tr>
                                                    );
                                                }
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </>
                )}
        </div>
    );
};

export default BankFlowsView;